import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginStart, loginSuccess, loginFailure } from "@/features/auth/authSlice";
import { useAuth } from "@/context/AuthContext";
import { loginApi, twoFactorLogin } from "@/services/auth";
import { parseJwt } from "@/utils/jwt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";

export default function Login() {
  const [uniqueValue, setUniqueValue] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  // 2FA adimi
  const [step, setStep] = useState("credentials"); // "credentials" | "twoFactor"
  const [pendingUserId, setPendingUserId] = useState("");
  const [code, setCode] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((s) => s.auth.loading);
  const authCtx = useAuth();

  const applyToken = (token) => {
    const user = parseJwt(token);
    authCtx.login(token);
    dispatch(
      loginSuccess({
        id: user.userId,
        name: user.name,
        username: user.username,
        email: user.email,
        roles: user.roles,
        permissions: user.permissions,
      })
    );
    toast.success(`Hos geldiniz, ${user.name}!`);
    navigate("/");
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    dispatch(loginStart());
    try {
      const res = await loginApi(uniqueValue, password);
      const { isTwoFactorEnabled, data } = res.data;

      if (isTwoFactorEnabled) {
        setPendingUserId(data.UserId);
        setStep("twoFactor");
        dispatch(loginFailure(null));
        toast.info("Iki faktorlu dogrulama kodunuzu giriniz.");
      } else {
        applyToken(data.Token);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        "Giris basarisiz. Bilgilerinizi kontrol ediniz.";
      dispatch(loginFailure(msg));
      toast.error(msg);
    }
  };

  const handleTwoFactor = async (e) => {
    e.preventDefault();
    dispatch(loginStart());
    try {
      const res = await twoFactorLogin(pendingUserId, code);
      applyToken(res.data.data.Token);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        "Dogrulama kodu gecersiz.";
      dispatch(loginFailure(msg));
      toast.error(msg);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 md:p-8"
      style={{ backgroundColor: "#eeeeee" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="glass-card p-8 md:p-10 animate-float-in bg-white">

          {step === "credentials" ? (
            <>
              <div className="space-y-1 mb-7">
                <div className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold">
                  Hos Geldiniz
                </div>
                <h1
                  className="text-3xl font-bold text-slate-900 tracking-tight"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Yonetici Girisi
                </h1>
                <p className="text-sm text-slate-500">
                  Hesabiniza erisim icin bilgilerinizi giriniz.
                </p>
              </div>

              <form onSubmit={handleCredentials} className="space-y-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="uniqueValue"
                    className="text-xs font-semibold text-slate-700 uppercase tracking-wider"
                  >
                    Kullanici Adi / E-posta
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="uniqueValue"
                      type="text"
                      required
                      data-testid="login-email-input"
                      value={uniqueValue}
                      onChange={(e) => setUniqueValue(e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 focus-visible:ring-blue-400 rounded-xl"
                      placeholder="kullanici@sirket.com"
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="password"
                    className="text-xs font-semibold text-slate-700 uppercase tracking-wider"
                  >
                    Sifre
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      data-testid="login-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-12 bg-white border-slate-200 focus-visible:ring-blue-400 rounded-xl"
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="login-toggle-password"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="remember"
                    checked={remember}
                    onCheckedChange={setRemember}
                    data-testid="login-remember-checkbox"
                  />
                  <Label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                    Beni hatirla
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md shadow-blue-200 transition-all group"
                >
                  {loading ? "Giris yapiliyor..." : "Giris Yap"}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="space-y-1 mb-7">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">
                    Iki Faktorlu Dogrulama
                  </span>
                </div>
                <h1
                  className="text-3xl font-bold text-slate-900 tracking-tight"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Dogrulama Kodu
                </h1>
                <p className="text-sm text-slate-500">
                  Hesabiniza kayitli dogrulama uygulamasindan ya da
                  e-postanizdan gelen 6 haneli kodu giriniz.
                </p>
              </div>

              <form onSubmit={handleTwoFactor} className="space-y-4" data-testid="two-factor-form">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="code"
                    className="text-xs font-semibold text-slate-700 uppercase tracking-wider"
                  >
                    Dogrulama Kodu
                  </Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="code"
                      type="text"
                      maxLength={8}
                      required
                      data-testid="two-factor-code-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 focus-visible:ring-blue-400 rounded-xl tracking-[0.4em] text-center font-mono text-lg"
                      placeholder="000000"
                      autoComplete="one-time-code"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || code.length < 8}
                  data-testid="two-factor-submit-button"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md shadow-blue-200 transition-all group"
                >
                  {loading ? "Dogrulanıyor..." : "Dogrula"}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setCode("");
                    dispatch(loginFailure(null));
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
                >
                  Geri don
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
